/**
 * Cross-module X XWP-13 (refonte WP-38; ux/04 §1.2-§1.3; X 02_DESIGN §3.4-§3.5) — the pure model of
 * the product panel and of its dialogs: which actions a role sees, the lot groups and their
 * disclosure, the lot-card lines (T / B / R / V for PROPRIETAIRE / MANAGER, « n disponibles » /
 * « Non disponible » for a CAISSIER), the period presets, the block dialog bounds, the
 * purchase-price chart data and the document link of a lot's source.
 *
 * The server is the authority on every figure (`GET /produits/:id/panneau` builds a CAISSIER payload
 * without any cost, margin, block or reservation field); this module only decides what to draw from
 * what came back, so the UI never shows a hole where the server left a field out. Nothing here
 * touches the DOM (components/produit/panneau/*.jsx render it).
 */
import { can } from "@/config/permissions.config.js";
import { canAccessRoute } from "@/config/routeAccess.config.js";
import { MAGASINS_ROUTE } from "@/config/magasins.config.js";
import { CARTE_LOT, PANNEAU_PRODUIT, COMPARER_PRIX, PRODUITS_LISTE } from "@/config/produits.messages.js";
import { CHART_COLORS } from "@/config/app.config.js";
import { fmtDate, fmtInt, fmtMoney, LOCALE_LABELS } from "@/config/locale.config.js";
import { formatDecimal } from "@/lib/number-format.js";
import { texteMoisPeremption } from "@/lib/etiquettes-lots.js";
import { BLOCAGE_LOT_LIMITES, BLOCAGE_LOT_MOTIFS } from "@pharmakon/shared/constants/disponibilite-stock.js";

/** Lot groups in display order (server `GROUPES_LOTS_PANNEAU`); only « vendables » opens expanded. */
export const GROUPES_LOTS = Object.freeze(["vendables", "bloques", "perimes", "epuises"]);
export const GROUPES_OUVERTS_PAR_DEFAUT = Object.freeze(["vendables"]);

/** Server `NIVEAU_PEREMPTION` → badge tone (the thresholds stay server-side, AUD-STOCK-06). */
export const TON_PEREMPTION = Object.freeze({
  PERIME: "danger",
  CRITIQUE: "danger",
  ALERTE: "warning",
  NORMAL: "neutral",
  SANS: "neutral",
});

/** Per-viewer convenience: the last period chosen in the panel (A-13 « last choice remembered »). */
export const CLE_PERIODE_PANNEAU = "pk:produit-panneau:periode";

/**
 * The actions and data blocks a user sees on the panel and its dialogs (ux/04 §0.6, X §14.3). The
 * server refuses every one of them to a role without the permission; hiding is convenience only.
 * @param {{ role?: string } | null | undefined} user
 */
export function droitsPanneau(user) {
  return Object.freeze({
    gestion: can(user, "stock", "bloquer"),
    modifier: can(user, "produit", "update"),
    generiques: can(user, "produit", "generiques"),
    comparer: can(user, "produit", "historique_prix"),
    bloquerQuantite: can(user, "stock", "bloquer"),
    corriger: can(user, "stock", "correct"),
    etiquettes: can(user, "etiquette_lot", "imprimer"),
    bloquerVente: can(user, "produit", "block"),
    voirStock: can(user, "stock", "read"),
    // X XWP-16 — « Répartir » is the placement write (`magasin.allocate`); the Magasins workspace is P/M
    repartition: can(user, "magasin", "allocate"),
    // X XWP-97 (D-49) — « Invisible aux caissiers » (PATCH /stock/lots/:id/visibilite, P only)
    visibiliteLot: can(user, "stock", "visibilite_lot"),
    // refonte WP-130 (D-51) — « Mouvements de stock » tab (GET /stock/produits/:id/mouvements-periode, P / M)
    mouvements: can(user, "stock", "mouvements"),
    voirMagasins: canAccessRoute(user?.role, MAGASINS_ROUTE),
  });
}

/** True when the payload is the CAISSIER shape (no management layers sent by the server). */
export function estPayloadRedige(panneau) {
  return !panneau?.resume || panneau.resume.stock_total === undefined;
}

/** Lot groups present in a payload, with their counts (« Lots bloqués » only when sent). */
export function groupesDuPanneau(panneau) {
  const lots = panneau?.lots ?? {};
  return GROUPES_LOTS.filter((g) => Array.isArray(lots[g])).map((g) => ({
    cle: g,
    lots: lots[g],
    total: g === "epuises" ? Math.max(lots.epuises_total ?? 0, lots[g].length) : lots[g].length,
  }));
}

/** The group title (« Lots vendables (9) »), the épuisés count being the server total. */
export function titreGroupe(cle, total) {
  return PANNEAU_PRODUIT.groupes[cle](total);
}

/** The lots expanded on arrival: the first vendable one, plus the lot asked for (`focusLot`). */
export function lotsDeveloppesInitiaux(panneau, focusLotId = null) {
  const out = new Set();
  const premier = panneau?.lots?.vendables?.[0];
  if (premier) out.add(premier.id);
  if (focusLotId) out.add(carteDuLot(panneau, focusLotId)?.id ?? focusLotId);
  return out;
}

/**
 * X XWP-97 (XO-25) — the card showing `lotId`: the lot's own card, or the card of identical
 * receptions that sums it (`lot_ids`). Null when the payload does not show it.
 */
export function carteDuLot(panneau, lotId) {
  for (const g of GROUPES_LOTS) {
    const c = (panneau?.lots?.[g] ?? []).find((l) => l.id === lotId || (l.lot_ids ?? []).includes(lotId));
    if (c) return c;
  }
  return null;
}

/**
 * The lot a panel action targets from its id: a card, or one reception of a card of identical
 * receptions (its own figures, its facture as `source`). A card's id is its oldest reception's: with
 * `{ reception: true }` that id names the reception (the focus is in the reception's row), else the card.
 */
export function lotDuPanneau(panneau, lotId, { reception = false } = {}) {
  const carte = carteDuLot(panneau, lotId);
  if (!carte) return null;
  if (carte.id === lotId && (!reception || !(carte.receptions?.length > 1))) return carte;
  const r = (carte.receptions ?? []).find((x) => x.id === lotId);
  return r ? { ...carte, ...r, receptions: undefined, nb_receptions: 1, lot_ids: [r.id], groupe_id: carte.id } : carte;
}

/** The group holding `lotId` (to open its disclosure when a scanned lot code targets it). */
export function groupeDuLot(panneau, lotId) {
  for (const g of GROUPES_LOTS) {
    if ((panneau?.lots?.[g] ?? []).some((l) => l.id === lotId || (l.lot_ids ?? []).includes(lotId))) return g;
  }
  return null;
}

/** « Lot B1REG5 » or « Lot non renseigné » (never an invented number, AUD-STOCK-08). */
export function numeroLot(lot) {
  return lot?.numero_lot ? String(lot.numero_lot) : CARTE_LOT.lotNonRenseigne;
}

/** « 01/2027 » or « Sans péremption » (AUD-PROD-25: never « 31/12/9999 »). */
export function moisPeremption(lot) {
  if (lot?.sans_peremption) return CARTE_LOT.sansPeremption;
  return texteMoisPeremption(lot);
}

/** Relative expiry badge: text + tone, or null for a lot without expiry. */
export function badgePeremption(lot) {
  if (!lot || lot.sans_peremption || lot.jours_restants == null) return null;
  const j = Number(lot.jours_restants);
  const libelle = j < 0 ? CARTE_LOT.perimeDepuis(-j) : j === 0 ? CARTE_LOT.aujourdhui : CARTE_LOT.dans(j);
  return { libelle, ton: TON_PEREMPTION[lot.niveau_peremption] ?? "neutral" };
}

/**
 * The quantity line of a lot card (02_DESIGN §3.4): P/M « Reste 100 · Bloqué 30 · Réservé en caisse
 * 5 · Vendable 65 »; a CAISSIER « 65 disponibles » or « Non disponible ».
 */
export function ligneQuantites(lot) {
  const q = lot?.quantites ?? {};
  if (q.reste === undefined) {
    const v = Number(q.vendable ?? 0);
    return { texte: v > 0 ? CARTE_LOT.disponibles(v) : CARTE_LOT.nonDisponible, indisponible: v <= 0 };
  }
  return {
    texte: CARTE_LOT.couches({ reste: q.reste, bloque: q.bloque ?? 0, reserve: q.reserve ?? 0, vendable: q.vendable ?? 0 }),
    indisponible: Number(q.vendable ?? 0) <= 0,
  };
}

/** « Reste 12 (8 + 4 UG) » of a collapsed card (P/M only; null for a CAISSIER payload). */
export function resteCourt(lot) {
  const q = lot?.quantites;
  if (!q || q.reste === undefined) return null;
  return CARTE_LOT.resteCourt(q.reste, q.payees ?? 0, q.ug ?? 0);
}

/** « Reçu 20 = 15 reçues + 5 UG » and « Qté payée 16 » when the billed quantity differs (D-08). */
export function ligneRecu(lot) {
  const r = lot?.recu;
  if (!r) return null;
  const payeeDiffere = r.quantite_payee != null && Number(r.quantite_payee) !== Number(r.payees);
  return {
    total: r.total,
    detail: CARTE_LOT.recuDetail(r.payees, r.ug),
    qtePayee: payeeDiffere ? CARTE_LOT.qtePayee(r.quantite_payee) : null,
  };
}

/** « Principal 8 · Réserve 4 » (P/M: quantities; CAISSIER: names only). */
export function ligneMagasins(lot) {
  const m = lot?.magasins ?? [];
  if (!m.length) return null;
  return m.map((x) => (x.quantite != null ? `${x.nom} ${fmtInt(x.quantite)}` : x.nom)).join(" · ");
}

/** « 50,0 % » (1 decimal, A-13) or « — ». */
export function texteMarge(pct) {
  if (pct == null || Number.isNaN(Number(pct))) return "—";
  return `${formatDecimal(String(pct), { decimals: 1 })}\u202F%`;
}

/** « 4,13 » units per day (2 decimals). */
export function texteRotation(parJour) {
  return formatDecimal(String(parJour ?? 0), { decimals: 2 });
}

/** The money / margin block of an expanded card (P/M), or null when the server sent PPA only. */
export function blocPrix(lot) {
  const p = lot?.prix;
  if (!p) return null;
  if (p.pu_ht_brut === undefined) return { ppa: fmtMoney(p.ppa), redige: true };
  const mu = p.marge_unitaire ?? {};
  // X XWP-97 (XO-25) — receptions bought at different prices: the PU HT range, margins per reception
  if (p.pu_ht_plage) {
    return {
      redige: false,
      plage: true,
      puHt: `${fmtMoney(p.pu_ht_plage.min)} – ${fmtMoney(p.pu_ht_plage.max)}`,
      ppa: fmtMoney(p.ppa),
      tr: p.tarif_ref != null ? fmtMoney(p.tarif_ref) : null,
    };
  }
  return {
    redige: false,
    puHt: fmtMoney(p.pu_ht_brut),
    ppa: fmtMoney(p.ppa),
    tr: p.tarif_ref != null ? fmtMoney(p.tarif_ref) : null,
    marge: texteMarge(p.marge_pct),
    comptoir: fmtMoney(mu.comptoir),
    chifa: mu.chifa != null ? fmtMoney(mu.chifa) : null,
    ug: fmtMoney(mu.ug_comptoir),
    ugChifa: mu.ug_chifa != null ? fmtMoney(mu.ug_chifa) : null,
  };
}

/**
 * The SPA route of a lot's source document (02_DESIGN §3.5). The server names the document
 * (`lien: { document, id }`); a kind without a screen of its own keeps the label without a link.
 */
export const ROUTES_DOCUMENT_SOURCE = Object.freeze({
  FACTURE_ACHAT: (id) => `/achats/${id}`,
  E_FACTURE: (id) => `/efactures-recu/${id}`,
  RETOUR_CLIENT: (id) => `/retours/${id}`,
  // refonte WP-87 (A-114 (9)) — a lot entered by a « Reçu non facturé » line: its avoir
  AVOIR_FOURNISSEUR: (id) => `/avoirs/${id}`,
});

export function routeDocument(lien) {
  if (!lien?.document || !lien.id) return null;
  const f = ROUTES_DOCUMENT_SOURCE[lien.document];
  return f ? f(lien.id) : null;
}

/** The identity line « PARACETAMOL · Comprimé · 500 MG · B/16 · SANOFI WINTHROP ». */
export function ligneIdentite(produit) {
  if (!produit) return "";
  const dci = typeof produit.dci === "object" ? produit.dci?.nom : produit.dci;
  const forme = typeof produit.forme === "object" ? produit.forme?.libelle : produit.forme;
  const labo = produit.laboratoire?.nom ?? (produit.marque ? PRODUITS_LISTE.marque(produit.marque) : null);
  return [dci, forme, produit.dosage, produit.conditionnement, labo].filter((x) => x != null && String(x).trim() !== "").join(" · ");
}

/** The chips row (ux/04 §1.2.1), each `{ id, libelle, ton, icone? }`. */
export function chipsProduit(produit) {
  if (!produit) return [];
  const C = PANNEAU_PRODUIT.chips;
  const out = [];
  if (produit.type_produit) out.push({ id: "type", libelle: LOCALE_LABELS[produit.type_produit] ?? produit.type_produit, ton: "info" });
  if (produit.liste_classification) {
    out.push({ id: "liste", libelle: LOCALE_LABELS[produit.liste_classification] ?? produit.liste_classification, ton: "neutral" });
  }
  out.push(
    produit.is_remboursable
      ? { id: "rem", libelle: C.remboursable(produit.tarif_ref != null ? fmtMoney(produit.tarif_ref) : null), ton: "success" }
      : { id: "rem", libelle: C.nonRemboursable, ton: "neutral" },
  );
  if (produit.is_psychotrope) {
    const ord = produit.type_ordonnance ? (LOCALE_LABELS[produit.type_ordonnance] ?? produit.type_ordonnance) : null;
    out.push({ id: "psy", libelle: C.psychotrope(ord), ton: "warning" });
  }
  if (produit.is_local != null) out.push({ id: "origine", libelle: produit.is_local ? C.locale : C.importee, ton: "neutral" });
  out.push(produit.bloque ? { id: "statut", libelle: C.bloque, ton: "danger", icone: "lock" } : { id: "statut", libelle: C.actif, ton: "success" });
  if (produit.bloque_vente_libre) out.push({ id: "vl", libelle: C.venteLibreBloquee, ton: "warning", icone: "lock" });
  return out;
}

/** The summary strip cells (CAISSIER: without the block / reservation figures). */
export function celluleResume(resume) {
  if (!resume) return [];
  const R = PANNEAU_PRODUIT.resume;
  const cells = [
    { id: "vendable", libelle: R.vendable, valeur: fmtInt(resume.stock_vendable) },
    { id: "perime", libelle: R.perime, valeur: fmtInt(resume.perime) },
    { id: "lots", libelle: R.lots, valeur: fmtInt(resume.lots) },
    { id: "qte_min", libelle: R.qteMin, valeur: resume.qte_min == null ? "—" : fmtInt(resume.qte_min) },
    { id: "seuil", libelle: R.seuil, valeur: resume.seuil_alerte == null ? "—" : fmtInt(resume.seuil_alerte) },
    {
      id: "en_commande",
      libelle: R.enCommande,
      valeur: resume.en_commande == null ? R.nonEvalue : fmtInt(resume.en_commande),
      aide: resume.en_commande == null ? (resume.en_commande_libelle ?? null) : null,
    },
    { id: "codes", libelle: R.codes, valeur: fmtInt(resume.codes_barre) },
  ];
  if (resume.stock_bloque !== undefined) {
    cells.splice(1, 0, { id: "bloque", libelle: R.bloque, valeur: fmtInt(resume.stock_bloque) }, { id: "reserve", libelle: R.reserve, valeur: fmtInt(resume.stock_reserve) });
  }
  return cells;
}

// ─── Period (A-13) ─────────────────────────────────────────────────────────────

/** The preset a `{ debut, fin, jours }` period matches (« 30 »), else "perso". */
export function presetDeLaPeriode(periode, presets = []) {
  if (!periode?.jours) return null;
  return presets.includes(periode.jours) ? String(periode.jours) : "perso";
}

/** Read the remembered period choice (preset days or a custom range); never throws. */
export function lirePeriodeMemorisee(storage = typeof localStorage !== "undefined" ? localStorage : null) {
  try {
    const v = JSON.parse(storage?.getItem(CLE_PERIODE_PANNEAU) ?? "null");
    if (v && (Number.isInteger(v.jours) || (v.debut && v.fin))) return v;
  } catch {
    /* private window, blocked storage or a foreign value: no remembered choice */
  }
  return null;
}

export function memoriserPeriode(choix, storage = typeof localStorage !== "undefined" ? localStorage : null) {
  try {
    storage?.setItem(CLE_PERIODE_PANNEAU, JSON.stringify(choix));
  } catch {
    /* per-viewer convenience only */
  }
}

/** The remount key of a period (its bounds): a new period from the server restarts the selector. */
export const periodeCle = (periode) => `${periode?.debut ?? ""}|${periode?.fin ?? ""}`;

/**
 * The request params of a period choice (`{}` = the server default « 30 j »). A preset is sent as
 * `jours`: the server counts back from today's officine day, never from the browser clock.
 */
export function parametresPeriode(choix) {
  if (!choix) return {};
  if (choix.debut && choix.fin) return { debut: choix.debut, fin: choix.fin };
  if (Number.isInteger(choix.jours)) return { jours: choix.jours };
  return {};
}

// ─── « Bloquer une quantité » (§14, XD-11) ────────────────────────────────────

/**
 * The lots a block may target, FEFO as the server sent them: non-expired lots with units not yet
 * blocked or held by a caisse window (T − B − R > 0).
 */
export function lotsBloquables(panneau) {
  const vendables = panneau?.lots?.vendables ?? [];
  const bloques = panneau?.lots?.bloques ?? [];
  // X XWP-97 (XO-25) — a block always targets ONE reception: a card of identical receptions offers each
  // of them, named by its facture
  return [...vendables, ...bloques]
    .flatMap((l) =>
      l.receptions?.length > 1
        ? l.receptions.map((r) => ({ ...l, ...r, receptions: undefined, nb_receptions: 1, reception: r.source?.libelle ?? CARTE_LOT.receptionRecue(fmtDate(r.created_at)) }))
        : [l],
    )
    .filter((l) => !l.perime && l.quantites?.reste !== undefined)
    .map((l) => ({ ...l, libre: Math.max(0, l.quantites.reste - (l.quantites.bloque ?? 0) - (l.quantites.reserve ?? 0)) }))
    .filter((l) => l.libre > 0);
}

/** X XWP-97 — the « Invisible » badge of a card (P / M payload): every reception, some of them, none. */
export function badgeInvisible(lot) {
  if (lot?.invisible_comptoir === true) return { libelle: CARTE_LOT.invisibleBadge, aide: CARTE_LOT.invisibleAide };
  if (lot?.invisible_partiel === true) {
    const n = (lot.receptions ?? []).filter((r) => r.invisible_comptoir).length;
    return { libelle: CARTE_LOT.invisiblePartiel(n, lot.receptions.length), aide: CARTE_LOT.invisibleAide };
  }
  return null;
}

/** « 2 réceptions » of a card of identical receptions, else null. */
export const texteReceptionsCarte = (lot) => (lot?.receptions?.length > 1 ? CARTE_LOT.receptions(lot.receptions.length) : null);

/**
 * The receptions whose visibility changes when the card's toggle is set to `invisible` (a card of
 * identical receptions acts on all of them, the ones already at that value left alone).
 */
export function receptionsAModifier(lot, invisible) {
  const toutes = lot?.receptions?.length > 1 ? lot.receptions : [lot];
  return toutes.filter((r) => r && (r.invisible_comptoir === true) !== invisible).map((r) => r.id);
}

/** « Portée : les caissiers au comptoir » from the « Lots au comptoir » settings (GET, P / M). */
export function lignePortee(parametres) {
  const cle = parametres?.portee_lot_invisible;
  const p = (parametres?.portees_lot_invisible ?? []).find((x) => x.cle === cle);
  return p ? CARTE_LOT.invisiblePortee(p.libelle) : null;
}

/** The motif options of the dialog (shared enum twin, never a hand-typed subset — PK-BUG-15). */
export function optionsMotifBlocage() {
  return Object.entries(BLOCAGE_LOT_MOTIFS).map(([value, m]) => ({ value, label: m.libelle, precisionRequise: m.precision_requise }));
}

/**
 * Client-side check of the block form (the server re-checks under its row lock). Returns the error
 * of each field, or an empty object.
 * @param {{ quantite: string|number, motif: string, precision?: string }} saisie
 * @param {number|null} max  T − B − R of the selected lot, from `GET /stock/lots/:id/blocages`
 */
export function erreursBlocage({ quantite, motif, precision }, max, messages) {
  const e = {};
  const q = Number(String(quantite ?? "").trim());
  if (!Number.isInteger(q) || q <= 0 || q > BLOCAGE_LOT_LIMITES.quantite_max) e.quantite = messages.quantiteInvalide;
  else if (max != null && q > max) e.quantite = messages.quantiteTrop(max);
  if (BLOCAGE_LOT_MOTIFS[motif]?.precision_requise && !String(precision ?? "").trim()) e.precision = messages.precisionRequise;
  if (String(precision ?? "").length > BLOCAGE_LOT_LIMITES.precision_max) e.precision = messages.precisionRequise;
  return e;
}

/** The body of `POST /stock/lots/:id/blocages`. */
export function corpsBlocage({ quantite, motif, precision, magasin_id }) {
  const p = String(precision ?? "").trim();
  return {
    quantite: Number(quantite),
    motif,
    ...(p ? { precision: p } : {}),
    ...(magasin_id ? { magasin_id } : {}),
  };
}

// ─── « Comparer les prix d'achat » (§13, XD-07) ───────────────────────────────

const dateCourte = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${String(d.getUTCDate()).padStart(2, "0")}/${String(d.getUTCMonth() + 1).padStart(2, "0")}/${String(d.getUTCFullYear()).slice(-2)}`;
};

/** One colour per product of the legend: the product itself first (accent), then the series. */
export function seriesGraphique(produits = []) {
  return produits.map((p, i) => ({
    id: p.id,
    libelle: [p.nom_commercial, p.laboratoire].filter(Boolean).join(" · "),
    reference: Boolean(p.reference),
    couleur: i === 0 ? "var(--pk-accent-ui)" : CHART_COLORS.series[(i - 1) % CHART_COLORS.series.length],
  }));
}

/**
 * The bars of the chart: one per purchase event in chronological order (never aggregated), each
 * carrying its own colour, its tooltip sentence and the link of its piece.
 */
export function donneesGraphique(historique) {
  const series = new Map(seriesGraphique(historique?.produits).map((s) => [s.id, s]));
  return (historique?.evenements ?? []).map((e, index) => {
    const s = series.get(e.produit_id);
    const prix = Number(e.prix_achat_ht);
    return {
      cle: e.id ?? `${e.produit_id}-${index}`,
      index,
      etiquette: dateCourte(e.date),
      date: fmtDate(e.date),
      prix,
      prixTexte: fmtMoney(e.prix_achat_ht),
      quantite: e.quantite,
      ug: e.ug,
      fournisseur: e.fournisseur?.nom ?? null,
      produit_id: e.produit_id,
      produit: s?.libelle ?? "",
      couleur: s?.couleur ?? "var(--pk-accent-ui)",
      piece: e.source?.libelle ?? null,
      route: e.source?.type === "FACTURE_ACHAT" && e.source.id ? `/achats/${e.source.id}` : null,
      infobulle: COMPARER_PRIX.infobulle({
        date: fmtDate(e.date),
        quantite: e.quantite,
        ug: e.ug,
        fournisseur: e.fournisseur?.nom ?? null,
        prix: fmtMoney(e.prix_achat_ht),
      }),
    };
  });
}

/** ← / → between bars (clamped; Début / Fin jump). */
export function barreVoisine(index, total, touche) {
  if (!total) return null;
  if (touche === "Home") return 0;
  if (touche === "End") return total - 1;
  if (touche === "ArrowLeft") return Math.max(0, (index ?? 0) - 1);
  if (touche === "ArrowRight") return Math.min(total - 1, (index ?? -1) + 1);
  return index;
}
